import * as React from "react";
import Marquee from "react-marquee-slider";
import styles from "./styles.module.scss";
import { MemoMessageModal as MessageModal } from "../../modals/MessageModal";
import Dialog from "../../Dialog";
import { useQuery } from "@apollo/client";
import { GetApprovedMessages } from "@src/graphql/queries";
import { getCookie } from "cookies-next";
import { motion } from "framer-motion";
import {
  EventBySlugQuery,
  GetApprovedMessagesQuery,
  GetApprovedMessagesQueryVariables,
} from "@src/@types/api.d";
import { ApprovedMessage } from "@src/components/BirthdayTemplate1/MarqueeComp";

type Props = {
  eventId: number;
  slugData: EventBySlugQuery | undefined;
};

const MarqueeComp: React.FC<Props> = ({ slugData, eventId }) => {
  const [open, setOpen] = React.useState<boolean>(false);
  const [show, setShow] = React.useState<boolean>(false);
  const currentAccessToken = getCookie("tsl") as string;
  const [isMarqueePaused, setIsMarqueePaused] = React.useState(false);

  const pauseMarquee = () => setIsMarqueePaused(true);
  const resumeMarquee = () => setIsMarqueePaused(false);

  React.useEffect(() => {
    setShow(true);
  }, []);

  const { data: getApprovedMessagesData } = useQuery<
    GetApprovedMessagesQuery,
    GetApprovedMessagesQueryVariables
  >(GetApprovedMessages, {
    skip: !eventId,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables: { eventId },
    pollInterval: 1000 * 60,
  });

  const messagesToDisplay =
    getApprovedMessagesData?.getApprovedMessages &&
    getApprovedMessagesData.getApprovedMessages.length > 0 &&
    getApprovedMessagesData.getApprovedMessages;

  const defaultMessages: Partial<
    NonNullable<GetApprovedMessagesQuery["getApprovedMessages"]>[number]
  >[] = [
    {
      content: `Welcome to ${
        slugData?.eventBySlug?.name || "this special celebration"
      }! 🎉`,
    },
    {
      content:
        "Share your heartfelt wishes and make this moment unforgettable ✨",
    },
    {
      content: "Your beautiful messages will light up this celebration 💫",
    },
    {
      content: "Be the first to drop a special note and spread some joy! 🌟",
    },
    {
      content:
        "Every celebration is better with warm wishes from loved ones 💝",
    },
    {
      content: "Join us in making this day extra special with your words 🥳",
    },
  ];

  // Helper function to ensure at least 6 entries
  const repeatEntries = (
    entries: (ApprovedMessage | null | Partial<ApprovedMessage>)[] | null
  ): (ApprovedMessage | Partial<ApprovedMessage>)[] => {
    console.log(entries?.length);
    if (!entries || entries.length === 0) return [];
    if (entries.length >= 6) return entries;

    const length = entries.length;
    let repeated: (ApprovedMessage | Partial<ApprovedMessage>)[] = [];

    if (length === 1) {
      // Repeat single entry 6 times
      repeated = Array(6).fill(entries[0]);
    } else if (length === 2) {
      // Repeat the pair thrice
      repeated = [...entries, ...entries, ...entries];
    } else if (length === 3) {
      // Repeat each entry 2 times
      repeated = [...entries, ...entries];
    } else if (length === 4) {
      // Repeat each entry 2 times (total 8)
      repeated = [...entries, ...entries];
    } else if (length === 5) {
      // Repeat each entry 2 times (total 10)
      repeated = [...entries, ...entries];
    }

    return repeated;
  };

  // Use default messages when no approved messages exist
  const displayMessages = messagesToDisplay || defaultMessages;

  // Prepare even and odd indexed messages
  const evenMessages = repeatEntries(
    displayMessages.filter((_, i) => i % 2 === 0)
  );
  const oddMessages = repeatEntries(
    displayMessages.filter((_, i) => i % 2 !== 0)
  );

  return (
    <section className="py-8 sm:py-12 bg-white">
      <motion.h3
        initial={{ opacity: 0, y: 30 }}
        whileInView={{
          opacity: 1,
          y: 0,
          transition: {
            duration: 1,
            ease: [0.79, 0.14, 0.15, 0.86],
          },
        }}
        className="font-brooklyn text-2xl sm:text-4xl text-black tracking-wider text-center"
      >
        Well-Wishes for the Celebrant
      </motion.h3>
      <div
        className={styles["home__marquee"]}
        onMouseEnter={pauseMarquee}
        onMouseLeave={resumeMarquee}
        onClick={pauseMarquee}
      >
        {/* First Marquee Row */}
        <div style={{ height: "250px", maxWidth: "100vw", width: "100%" }}>
          <Marquee
            velocity={isMarqueePaused ? 0 : 40}
            direction="rtl"
            scatterRandomly={false}
            resetAfterTries={200}
            onInit={() => null}
            onFinish={() => null}
          >
            {evenMessages.map((item, index) => (
              <div
                key={`quotes-${index}`}
                className={styles["home__special-note-box"]}
              >
                <p
                  className={`${styles["home__special-note-text"]} font-ebGaramond`}
                >
                  {item?.content ?? ""}
                </p>
              </div>
            ))}
          </Marquee>
        </div>

        {/* Second Marquee Row */}
        {displayMessages.length > 1 && (
          <div style={{ height: "250px", maxWidth: "100vw", width: "100%" }}>
            <Marquee
              velocity={isMarqueePaused ? 0 : 40}
              direction="rtl"
              scatterRandomly={false}
              resetAfterTries={200}
              onInit={() => null}
              onFinish={() => null}
            >
              {oddMessages.map((item, index) => (
                <div
                  key={`quotes-2-${index}`}
                  className={styles["home__special-note-box"]}
                >
                  <p
                    className={`${styles["home__special-note-text"]} font-ebGaramond`}
                  >
                    {item?.content ?? ""}
                  </p>
                </div>
              ))}
            </Marquee>
          </div>
        )}

        {/* Enhanced Special Note Button with Call to Action */}
        {show && (
          <div className="text-center mt-6">
            {/* Call to Action Text for when there are no user messages */}
            {!messagesToDisplay && (
              <div className="mb-4">
                <h3 className="text-2xl font-bold text-gray-800 mb-2 font-ebGaramond">
                  Be the First to Share Your Wishes! 🌟
                </h3>
                <p className="text-gray-600 font-neue">
                  Drop a heartfelt message to make this celebration even more
                  special
                </p>
              </div>
            )}

            {/* Show button only for authenticated users */}
            {!!currentAccessToken && (
              <Dialog
                trigger={
                  <div
                    className={`${
                      styles["home__special-note__btn"]
                    } font-ebGaramond ${
                      !messagesToDisplay
                        ? " text-black px-8 py-3 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 cursor-pointer"
                        : ""
                    }`}
                  >
                    {!messagesToDisplay
                      ? "🎉 Share Your Wishes Now!"
                      : "Send a Special Note"}
                  </div>
                }
                open={open}
                onOpenChange={setOpen}
              >
                <MessageModal
                  name={slugData?.eventBySlug?.name ?? ""}
                  eventId={eventId}
                  setOpen={setOpen}
                />
              </Dialog>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default MarqueeComp;
